import type {
  GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200,
  GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200,
  GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200,
} from '@fukuri-admins/apis/client/gen/model';
import { useTooltip } from '@fukuri-admins/ui/common/Tooltip/hooks';
import { getThreeLetterCode } from '@fukuri-admins/utils/common/cookies';
import {
  useGetCustomFieldDefinitions,
  useGetPartnerAdminsClient,
  useGetPartnerAdminsPartnerFeatureFlags,
} from '~/hooks';
import { ClientDetailPresentation } from '../presentations';

type PresentationProps = React.ComponentProps<typeof ClientDetailPresentation>;

type IUseClientDetail = (id: number) => {
  state: {
    data: GetApiV1ThreeLetterCodePartnerAdminsClientsClientId200;
    customFieldDefinitions: GetApiV1ThreeLetterCodePartnerAdminsCustomFieldDefinitions200;
    partnerFeatureFlags: GetApiV1ThreeLetterCodePartnerAdminsPartnerFeatureFlags200;
    /** ツールチップの id（「利用者数」ヘルプアイコンの aria-describedby に使用） */
    tooltipId: PresentationProps['tooltipId'];
  };
  actions: {
    /** 「利用者数」ヘルプアイコンのツールチップ表示ハンドラー */
    handleShowTooltip: PresentationProps['showTooltip'];
    /** ツールチップを非表示にするハンドラー */
    handleHideTooltip: PresentationProps['hideTooltip'];
  };
};

/**
 * クライアント詳細のコンポーネントフック
 *
 * @param id - クライアントID（Screen 層でパース・検証済みの数値）
 */
export const useClientDetail: IUseClientDetail = id => {
  const threeLetterCode = getThreeLetterCode();
  const { showTooltip, hideTooltip, tooltipId } = useTooltip();

  const { data } = useGetPartnerAdminsClient({
    threeLetterCode,
    clientId: id,
  });

  const { data: partnerFeatureFlags } = useGetPartnerAdminsPartnerFeatureFlags({
    threeLetterCode,
  });

  const { data: customFieldDefinitions } = useGetCustomFieldDefinitions({
    threeLetterCode,
    target: 'c',
  });

  return {
    state: {
      data,
      customFieldDefinitions,
      partnerFeatureFlags,
      tooltipId,
    },
    actions: {
      handleShowTooltip: showTooltip,
      handleHideTooltip: hideTooltip,
    },
  };
};
